import styled, { css } from "styled-components";
import { COLORS } from '../../../theme/Colors';
import React, { useState } from "react";

interface CustomTextInputProps {
  error?: string;
  leftIcon?: string;
  rightIcon?: string;
  style?: React.CSSProperties;
  containerStyle?: React.CSSProperties;
  onChangeText?: (text: string) => void;
  value?: string;
  placeholder?: string;
  secureTextEntry?: boolean;
  type?: React.HTMLInputTypeAttribute;
  maxLength?: number;
  placeholderColor?: string;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  multiline?: boolean; 
  color?: string;
  backgroundColor?: string;
  tooltip?: string;
}

const CustomTextInput: React.FC<CustomTextInputProps> = ({
  error = "",
  leftIcon,
  rightIcon,
  style,
  containerStyle,
  onChangeText,
  value,
  placeholder,
  secureTextEntry,
  type,
  maxLength,
  placeholderColor,
  onClick,
  multiline = false,
  backgroundColor,
  tooltip,
  ...rest
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const commonProps = {
    maxLength,
    placeholder,
    value,
    onChange: (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) =>
      onChangeText?.(e.target.value),
    placeholderColor,
    style,
    onClick,
    onFocus: () => setIsFocused(true),
    onBlur: () => setIsFocused(false),
    ...rest,
  };

  return (
    <Wrapper style={style}>
      <Container style={containerStyle} $error={!!error} $backgroundColor={backgroundColor}>
        <InputContainer error={error} multiline={multiline} $backgroundColor={backgroundColor}>
          {leftIcon && <Icon src={leftIcon} alt="left icon" />}

          {multiline ? (
            <StyledTextarea {...commonProps} />
          ) : (
            <StyledInput
              {...commonProps}
              type={type || (secureTextEntry ? "password" : "text")}
            />
          )}

          {rightIcon && <Icon src={rightIcon} alt="right icon" />}

          {tooltip && isFocused && <Tooltip>{tooltip}</Tooltip>}
        </InputContainer>
      </Container>

      {error && <ErrorText>{error}</ErrorText>}
    </Wrapper>
  );
};

export default CustomTextInput;

const Wrapper = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const Container = styled.div<{ $error?: boolean; $backgroundColor?: string }>`
  border: 1px solid ${({ $error, theme }) => $error ? theme.colors.errorRed : theme.colors.inputBorder};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background-color: ${({ $backgroundColor }) => $backgroundColor || 'transparent'};
`;

const InputContainer = styled.div<{ error?: string; multiline: boolean; $backgroundColor?: string }>`
  position: relative;
  display: flex;
  align-items: ${({ multiline }) => (multiline ? "flex-start" : "center")};
  padding: 0;
  padding-top: ${({ multiline, theme }) => (multiline ? theme.spacing.xs : 0)};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-sizing: border-box;
  min-height: ${({ multiline, theme }) => (multiline ? '120px' : '50px')};
  border-width: 1px;
  background-color: ${({ $backgroundColor }) => $backgroundColor || 'transparent'};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  * {
    font-size: ${({ theme }) => theme.fontSizes.sm};
  }
`;

const baseInputStyles = css<CustomTextInputProps>`
  color: ${({ color, theme }) => color || theme.colors.textDark};
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  resize: none;
  &::placeholder {
    color: ${({ placeholderColor, theme }) => placeholderColor || theme.colors.textMuted};
    opacity: 1;
    margin: 0;
    padding: 0;
  }
`;

const StyledInput = styled.input<CustomTextInputProps>`
  height: 100%;
  margin: 0 ${({ theme }) => theme.spacing.xs};
  ${baseInputStyles}
`;

const StyledTextarea = styled.textarea<CustomTextInputProps>`
  width: 100%;
  margin: 0 ${({ theme }) => theme.spacing.xs};
  font-family: inherit;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  line-height: 1.4;
  ${baseInputStyles}
  min-height: 120px;
`;

const Tooltip = styled.div`
  position: absolute;
  top: -32px;
  left: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
  background-color: ${({ theme }) => theme.colors.textDark};
  color: white;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  white-space: nowrap;
  z-index: ${({ theme }) => theme.zIndex.tooltip};

  &::after {
    content: "";
    position: absolute;
    bottom: -6px;
    left: ${({ theme }) => theme.spacing.sm};
    border-width: 6px 6px 0 6px;
    border-style: solid;
    border-color: ${({ theme }) => theme.colors.textDark} transparent transparent transparent;
  }
`;

const Icon = styled.img`
  width: ${({ theme }) => theme.spacing.lg};
  height: ${({ theme }) => theme.spacing.lg};
  margin: 0;
  margin-left: ${({ theme }) => theme.spacing.xs};
`;

const ErrorText = styled.span`
  color: ${({ theme }) => theme.colors.errorRed};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  margin-top: ${({ theme }) => theme.spacing.xs};
  margin-left: ${({ theme }) => theme.spacing.xs};
`;
